import { cn } from '@ixigo/iui/variantStyle';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { useLayoutEffect, useRef } from 'react';

export interface TopNavBarProps extends ComponentPropsWithoutRef<'nav'> {
	leftSlot?: ReactNode;
	children: ReactNode;
	rightSlot?: ReactNode;
}

export const TopNavBar = ({
	leftSlot,
	children,
	rightSlot,
	className = '',
	role = 'navigation',
	'aria-label': ariaLabel = 'Top navigation bar',
	...navProps
}: TopNavBarProps) => {
	const leftRef = useRef<HTMLDivElement>(null);
	const rightRef = useRef<HTMLDivElement>(null);

	useLayoutEffect(() => {
		if (!leftRef.current || !rightRef.current) return;

		// Reset min-width to measure natural width
		leftRef.current.style.minWidth = '';
		rightRef.current.style.minWidth = '';

		const leftWidth = leftRef.current.offsetWidth;
		const rightWidth = rightRef.current.offsetWidth;
		const maxWidth = Math.max(leftWidth, rightWidth);

		// Set equal min-width to maintain center alignment
		leftRef.current.style.minWidth = `${maxWidth}px`;
		rightRef.current.style.minWidth = `${maxWidth}px`;
	});

	return (
		<nav
			role={role}
			aria-label={ariaLabel}
			className={cn(
				'grid grid-cols-[auto_1fr_auto] items-center w-full px-xl py-md',
				className,
			)}
			{...navProps}
		>
			{/* Left slot - optional, for navigation controls */}
			<div ref={leftRef} className="flex items-center justify-start">
				{leftSlot}
			</div>

			{/* Center slot - mandatory, always centered, typically for titles/branding */}
			<div className="flex items-center justify-center whitespace-nowrap overflow-hidden">
				{children}
			</div>

			{/* Right slot - optional, for action buttons */}
			<div ref={rightRef} className="flex items-center justify-end">
				{rightSlot}
			</div>
		</nav>
	);
};
